import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToggleListener = () => void;

type AdminCommandMenuCallbacksContextValue = Readonly<{
  shareDialogOpenRef: React.MutableRefObject<(() => void) | undefined>;
  registerShareDialog: (open: () => void) => () => void;
  commandMenuToggleRef: React.MutableRefObject<(() => void) | undefined>;
  registerCommandMenuToggle: (toggle: () => void) => () => void;
  subscribeToggle: (listener: ToggleListener) => () => void;
}>;

const AdminCommandMenuCallbacksContext = createContext<AdminCommandMenuCallbacksContextValue | undefined>(undefined);

export function AdminCommandMenuCallbacksProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const shareDialogOpenRef = useRef<(() => void) | undefined>();
  const commandMenuToggleRef = useRef<(() => void) | undefined>();
  const toggleListenersRef = useRef(new Set<ToggleListener>());

  const registerShareDialog = useCallback((open: () => void) => {
    shareDialogOpenRef.current = open;
    return () => {
      shareDialogOpenRef.current = undefined;
    };
  }, []);

  const registerCommandMenuToggle = useCallback((toggle: () => void) => {
    commandMenuToggleRef.current = toggle;
    toggleListenersRef.current.forEach(l => l());
    return () => {
      commandMenuToggleRef.current = undefined;
      toggleListenersRef.current.forEach(l => l());
    };
  }, []);

  const subscribeToggle = useCallback((listener: ToggleListener) => {
    toggleListenersRef.current.add(listener);
    return () => {
      toggleListenersRef.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({ shareDialogOpenRef, registerShareDialog, commandMenuToggleRef, registerCommandMenuToggle, subscribeToggle }),
    [registerShareDialog, registerCommandMenuToggle, subscribeToggle],
  );

  return <AdminCommandMenuCallbacksContext.Provider value={value}>{children}</AdminCommandMenuCallbacksContext.Provider>;
}

export function useCommandMenuCallbacks(): Readonly<{ onShareDialogOpen: (() => void) | undefined }> {
  const ctx = useContext(AdminCommandMenuCallbacksContext);
  return useMemo(
    () => ({
      get onShareDialogOpen() {
        return ctx?.shareDialogOpenRef.current;
      },
    }),
    [ctx],
  );
}

/**
 * Registers a share-dialog opener with the admin command menu.
 * Uses a ref + stable wrapper so callback identity changes don't
 * trigger re-renders of the entire admin tree.
 */
export function useRegisterShareDialog(onOpen: (() => void) | undefined) {
  const ctx = useContext(AdminCommandMenuCallbacksContext);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const hasCallback = Boolean(onOpen);

  useEffect(() => {
    if (!ctx || !onOpenRef.current) return;
    const stableCallback = () => onOpenRef.current?.();
    return ctx.registerShareDialog(stableCallback);
  }, [ctx, hasCallback]);
}

/**
 * Reads the command-menu toggle from the callbacks context.
 * Subscribes to registration changes so the consuming component
 * re-renders when the toggle becomes available or is removed.
 */
export function useCommandMenuToggle(): (() => void) | undefined {
  const ctx = useContext(AdminCommandMenuCallbacksContext);
  const [, rerender] = useState(0);

  useEffect(() => {
    if (!ctx) return;
    const unsub = ctx.subscribeToggle(() => rerender(n => n + 1));
    if (ctx.commandMenuToggleRef.current) {
      rerender(n => n + 1);
    }
    return unsub;
  }, [ctx]);

  return ctx?.commandMenuToggleRef.current;
}

export function useRegisterCommandMenuToggle(onToggle: (() => void) | undefined) {
  const ctx = useContext(AdminCommandMenuCallbacksContext);
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  const hasCallback = Boolean(onToggle);

  useEffect(() => {
    if (!ctx || !onToggleRef.current) return;
    const stableCallback = () => onToggleRef.current?.();
    return ctx.registerCommandMenuToggle(stableCallback);
  }, [ctx, hasCallback]);
}
